import { PRODUCT_TYPE } from '@/utils/enum';

/**
 * Helpers cho combo có sản phẩm con IMEI (feature flag `attach-warranty-imei-combo`).
 * Quy ước (plan-combo-imei-phase1 §2 D1, §3):
 * - Con IMEI cần N IMEI, N = định mức con / 1 combo × SL dòng combo.
 * - Payload `comboItems`: 1 phần tử / IMEI `{ productId, imeiCode }`,
 *   con thường chỉ gửi `{ productId, externalImeiNo }` khi có IMEI BH đính kèm.
 * - GET chi tiết HĐ trả `imeiCodes: []` cho từng con.
 */

export const toPositiveInteger = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const number = Number(String(value).split(',').join(''));
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
};

export const isImeiComboChild = (child) =>
  !!child && Number(child.productType) === PRODUCT_TYPE.PRODUCT_IMEI;

export const hasImeiComboChild = (comboItems) =>
  Array.isArray(comboItems) && comboItems.some(isImeiComboChild);

/** Danh sách IMEI đang có của con combo (đã trim, bỏ rỗng). */
export const getComboChildImeiCodes = (child) => {
  if (!child) return [];
  let codes = [];
  if (Array.isArray(child.imeiCodes)) {
    codes = child.imeiCodes;
  } else if (child.imeiCode) {
    codes = String(child.imeiCode).split(',');
  }
  return codes
    .map((code) => (code === null || code === undefined ? '' : String(code)))
    .map((code) => code.trim())
    .filter(Boolean);
};

export const formatComboChildImeiCodes = (child) =>
  getComboChildImeiCodes(child).join(', ');

/** Dòng combo đã lưu (sửa HĐ) có con IMEI → khóa SL / IMEI (D4). */
export const isComboImeiLocked = (billItem) =>
  !!billItem &&
  !!billItem.isPersisted &&
  hasImeiComboChild(billItem.comboItems);

/** Số IMEI bắt buộc của con IMEI trong dòng combo. */
export const getRequiredImeiCount = (billItem, child) => {
  if (!billItem || !isImeiComboChild(child)) return 0;
  const unitQuantity =
    child.unitQuantity !== undefined && child.unitQuantity !== null
      ? child.unitQuantity
      : child.quantity;
  return toPositiveInteger(unitQuantity) * toPositiveInteger(billItem.quantity);
};

/** IMEI của con sẽ gửi lên API (dòng mới: chỉ lấy N ô đầu). */
export const getComboChildImeiCodesForPayload = (billItem, child) => {
  if (!isImeiComboChild(child)) return [];
  if (isComboImeiLocked(billItem)) return getComboChildImeiCodes(child);
  const requiredCount = getRequiredImeiCount(billItem, child);
  const slots = Array.isArray(child.imeiCodes)
    ? child.imeiCodes.slice(0, requiredCount)
    : [];
  return getComboChildImeiCodes({ imeiCodes: slots });
};

export const buildComboItemsPayload = (billItem) => {
  if (!billItem || !Array.isArray(billItem.comboItems)) return [];
  return billItem.comboItems.reduce((result, child) => {
    if (isImeiComboChild(child)) {
      // Quà / bán kèm bỏ tick không xuất kho => không gửi IMEI con
      if (billItem.checked === false) return result;
      getComboChildImeiCodesForPayload(billItem, child).forEach((imeiCode) => {
        result.push({ productId: child.productId, imeiCode });
      });
      return result;
    }
    const externalImeiNo = child.externalImeiNo
      ? String(child.externalImeiNo).trim()
      : '';
    if (externalImeiNo) {
      result.push({ productId: child.productId, externalImeiNo });
    }
    return result;
  }, []);
};

/**
 * Chuẩn hóa con combo khi đưa vào listBillItem.
 * - `unitQuantity`: định mức con / 1 combo. Nếu con IMEI đã có IMEI thì suy ra từ
 *   số IMEI / SL dòng (không phụ thuộc `quantity` API trả là định mức hay tổng).
 * - `imeiCodes`: giữ IMEI đã lưu (sửa HĐ) hoặc reset rỗng (tạo mới / sao chép HĐ).
 * - `deriveUnitFromImei = false`: dùng khi IMEI có thể chưa đủ (đơn hàng, HĐ từ đơn) —
 *   API trả `quantity` = định mức / 1 combo (plan §10.5.2).
 */
export const normalizeComboItems = (
  comboItems,
  lineQuantity = 1,
  { keepImeiCodes = false, deriveUnitFromImei = true } = {},
) => {
  if (!Array.isArray(comboItems)) return comboItems;
  const lineQty = toPositiveInteger(lineQuantity);
  return comboItems.map((child) => {
    const codes = getComboChildImeiCodes(child);
    let unitQuantity = toPositiveInteger(child.quantity) || 1;
    if (
      deriveUnitFromImei &&
      isImeiComboChild(child) &&
      codes.length &&
      lineQty &&
      codes.length % lineQty === 0
    ) {
      unitQuantity = codes.length / lineQty;
    }
    return {
      ...child,
      unitQuantity,
      imeiCodes: keepImeiCodes ? codes : [],
    };
  });
};

const getComboName = (billItem) =>
  billItem.name || billItem.productName || billItem.code || '';

/**
 * Validate IMEI con combo trước khi submit.
 * - `requireAll = false` (đơn hàng ở trạng thái không bắt buộc IMEI): chỉ kiểm trùng
 *   các IMEI đã nhập, không kiểm đủ N / flag.
 * @returns {string} message lỗi, '' nếu hợp lệ.
 */
export const validateComboImei = (
  listBillItem = [],
  { enabled = false, requireAll = true, documentName = 'hóa đơn' } = {},
) => {
  const items = (listBillItem || []).filter(
    (item) => item && item.checked !== false,
  );
  const comboImeiCodes = [];
  for (const billItem of items) {
    if (billItem.flexibleComboId || !hasImeiComboChild(billItem.comboItems)) {
      continue;
    }
    if (isComboImeiLocked(billItem)) {
      billItem.comboItems.forEach((child) => {
        getComboChildImeiCodes(child).forEach((code) =>
          comboImeiCodes.push(code),
        );
      });
      continue;
    }
    if (!requireAll) {
      billItem.comboItems.forEach((child) => {
        getComboChildImeiCodesForPayload(billItem, child).forEach((code) =>
          comboImeiCodes.push(code),
        );
      });
      continue;
    }
    if (!enabled) {
      return `Sản phẩm combo ${getComboName(
        billItem,
      )} có sản phẩm IMEI chưa được hỗ trợ`;
    }
    for (const child of billItem.comboItems) {
      if (!isImeiComboChild(child)) continue;
      const requiredCount = getRequiredImeiCount(billItem, child);
      const codes = getComboChildImeiCodesForPayload(billItem, child);
      if (codes.length < requiredCount) {
        return `Vui lòng nhập đủ ${requiredCount} IMEI cho sản phẩm ${
          child.productName || ''
        } trong combo ${getComboName(billItem)}`;
      }
      codes.forEach((code) => comboImeiCodes.push(code));
    }
  }
  if (!comboImeiCodes.length) return '';

  const usedCodes = new Set(
    items
      .filter((item) => !item.flexibleComboId && item.imeiCode)
      .map((item) => String(item.imeiCode).trim())
      .filter(Boolean),
  );
  for (const code of comboImeiCodes) {
    if (usedCodes.has(code)) {
      return `IMEI ${code} bị trùng trong ${documentName}`;
    }
    usedCodes.add(code);
  }
  return '';
};

/** IMEI đã tồn tại trong HĐ (dòng thường + con combo). */
export const isImeiCodeInBill = (listBillItem = [], imeiCode) => {
  const code = imeiCode ? String(imeiCode).trim() : '';
  if (!code) return false;
  return (listBillItem || []).some(
    (item) =>
      (item.imeiCode && String(item.imeiCode).trim() === code) ||
      (!item.flexibleComboId &&
        Array.isArray(item.comboItems) &&
        item.comboItems.some(
          (child) =>
            isImeiComboChild(child) &&
            getComboChildImeiCodesForPayload(item, child).includes(code),
        )),
  );
};

/** Payload `comboItems` cho đơn hàng: chỉ con IMEI (plan §10.5.1). */
export const buildOrderComboItemsPayload = (orderItem) =>
  buildComboItemsPayload(orderItem).filter((item) => item.imeiCode);

/**
 * Khóa so sánh tập IMEI con theo từng combo: { [comboProductId]: 'childId:imei|...' }.
 * Dùng chặn đổi IMEI con khi đơn Đang giao → Đang giao (plan §10.9 Q2).
 */
export const buildComboImeiKeyMap = (items = []) => {
  const groups = {};
  (items || []).forEach((item) => {
    if (!item || item.deleted || !hasImeiComboChild(item.comboItems)) return;
    const key = item.productId;
    groups[key] = groups[key] || [];
    item.comboItems.forEach((child) => {
      getComboChildImeiCodesForPayload(item, child).forEach((code) =>
        groups[key].push(`${child.productId}:${code}`),
      );
    });
  });
  return Object.keys(groups).reduce((result, key) => {
    result[key] = groups[key].sort().join('|');
    return result;
  }, {});
};

/**
 * productId combo đầu tiên có tập IMEI con khác nhau giữa 2 khóa, null nếu giống.
 * `ignoreEmptyBefore`: combo lúc load chưa có IMEI con thì cho nhập (không coi là đổi).
 */
export const findChangedComboImeiProductId = (
  beforeMap = {},
  afterMap = {},
  { ignoreEmptyBefore = false } = {},
) => {
  const keys = new Set([...Object.keys(beforeMap), ...Object.keys(afterMap)]);
  for (const key of keys) {
    const before = beforeMap[key] || '';
    if (ignoreEmptyBefore && !before) continue;
    if (before !== (afterMap[key] || '')) return key;
  }
  return null;
};
